// Click any field to edit it in place — Save or Cancel, no edit drawer needed.
import { useState, type ReactNode } from 'react';
import { Button } from '../kit';

export type FactSpec = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'number' | 'select' | 'multi' | 'readonly';
  value: unknown;
  display: ReactNode;
  options?: { value: string; label: string }[];
  wide?: boolean;
  extra?: ReactNode;
};

export function Facts({ facts, canEdit, onSave }: { facts: FactSpec[]; canEdit: boolean; onSave: (key: string, value: unknown, label: string) => Promise<boolean> }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <dl className="facts ifacts">
      {facts.map((f) => (
        <div key={f.key} className={f.wide ? 'wide' : ''}>
          <dt>{f.label}</dt>
          <dd>
            {open === f.key ? (
              <Editor f={f} onCancel={() => setOpen(null)} onSave={async (v) => (await onSave(f.key, v, f.label)) && setOpen(null)} />
            ) : canEdit && f.type !== 'readonly' ? (
              <button type="button" className="qe fact-btn" onClick={() => setOpen(f.key)} aria-label={`Edit ${f.label}`}>
                {f.display}
                {f.extra}
              </button>
            ) : (
              <>
                {f.display}
                {f.extra}
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Editor({ f, onCancel, onSave }: { f: FactSpec; onCancel: () => void; onSave: (v: unknown) => Promise<unknown> }) {
  const init = f.type === 'multi' ? ((f.value as string[] | null) ?? []) : f.value === null || f.value === undefined ? '' : String(f.value);
  const [v, setV] = useState<string | string[]>(init as string | string[]);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const out = f.type === 'number' ? (v === '' ? null : Number(v)) : f.type === 'multi' ? v : v === '' ? null : v;
    await onSave(out);
    setBusy(false);
  };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
    if (e.key === 'Enter' && f.type !== 'textarea') void go();
  };
  let input: ReactNode;
  if (f.type === 'textarea')
    input = <textarea autoFocus rows={4} value={v as string} onChange={(e) => setV(e.target.value)} onKeyDown={keys} aria-label={f.label} />;
  else if (f.type === 'select')
    input = (
      <select autoFocus value={v as string} onChange={(e) => setV(e.target.value)} onKeyDown={keys} aria-label={f.label}>
        <option value="">—</option>
        {(f.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  else if (f.type === 'multi')
    input = (
      <div className="multi" role="group" aria-label={f.label}>
        {(f.options ?? []).map((o) => {
          const on = (v as string[]).includes(o.value);
          return (
            <button key={o.value} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => setV(on ? (v as string[]).filter((x) => x !== o.value) : [...(v as string[]), o.value])}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  else
    input = (
      <input
        autoFocus
        type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
        step={f.type === 'number' ? 'any' : undefined}
        value={v as string}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={keys}
        aria-label={f.label}
      />
    );
  return (
    <div className="fact-edit">
      {input}
      <div className="fact-edit-row">
        <Button kind="primary" onClick={() => void go()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
